import { useI18n } from '../i18n';
import { blockCopy, starCaption } from '../i18n/reviewLabels';
import { useRateSheet, type RateTarget } from '../hooks/useReviews';
import { DishLikeRow, RateSheet, ReviewPhotoTiles, StarPicker } from '../components/Reviews';
import { DraftBar } from '../components/ui';
import { orderTime } from '../utils/format';

/**
 * The rate sheet wired to its state (C26): opened from the order page (once by itself after
 * delivery), the "Rate your orders" strip, and Account › My reviews (new rating or edit).
 */
export function RateFlow({ target, onClose }: { target: RateTarget; onClose: () => void }) {
  const { t } = useI18n();
  const r = useRateSheet(
    target,
    {
      thanks: t('rate.thanks'),
      updated: t('rate.updated'),
      changed: t('rate.changed'),
      failed: t('rate.failed'),
      badType: t('rate.badType'),
      tooBig: t('rate.tooBig'),
      tooMany: t('rate.tooMany'),
      uploadFailed: t('rate.uploadFailed'),
    },
    onClose,
  );
  const starLabel = (n: number) => t('rate.starAria', { n });

  return (
    <RateSheet
      title={r.isEdit ? t('rate.editTitle') : t('rate.title')}
      subtitle={t('rate.from', { name: r.outletName })}
      onClose={onClose}
      draft={
        r.draftRestored ? (
          <DraftBar
            text={t('common.draftRestored')}
            keepLabel={t('common.keep')}
            discardLabel={t('common.discard')}
            onKeep={r.keepDraft}
            onDiscard={r.discardDraft}
          />
        ) : null
      }
      block={r.block ? blockCopy(t, r.block) : null}
      editNote={
        target.kind === 'edit'
          ? t('rate.editUntil', { time: orderTime(target.review.editableUntil) })
          : null
      }
      food={
        <StarPicker
          title={t('rate.food')}
          value={r.form.foodStars}
          onChange={r.setFoodStars}
          caption={starCaption(t, r.form.foodStars)}
          starLabel={starLabel}
        />
      }
      delivery={
        r.hasRider ? (
          <StarPicker
            title={t('rate.delivery')}
            value={r.form.deliveryStars}
            onChange={r.setDeliveryStars}
            caption={starCaption(t, r.form.deliveryStars)}
            starLabel={starLabel}
          />
        ) : null
      }
      dishesTitle={t('rate.dishes')}
      dishes={r.dishes.map((dish) => (
        <DishLikeRow
          key={dish.menuItemId}
          name={dish.name}
          liked={r.form.liked.includes(dish.menuItemId)}
          unliked={r.form.unliked.includes(dish.menuItemId)}
          likeLabel={t('rate.like', { name: dish.name })}
          unlikeLabel={t('rate.unlike', { name: dish.name })}
          onLike={() => r.like(dish.menuItemId)}
          onUnlike={() => r.unlike(dish.menuItemId)}
        />
      ))}
      text={{
        label: t('rate.text'),
        placeholder: t('rate.textPlaceholder'),
        value: r.form.text,
        maxLength: r.maxLength,
        counter: t('rate.count', { n: r.form.text.length, max: r.maxLength }),
        onChange: r.setText,
      }}
      photos={
        <ReviewPhotoTiles
          slots={r.photos.slots}
          canAdd={r.photos.canAdd}
          labels={{
            title: t('rate.photos'),
            hint: t('rate.photosHint'),
            add: t('rate.addPhoto'),
            remove: t('rate.removePhoto'),
            uploading: (percent) => t('rate.uploading', { percent }),
            failed: t('rate.photoFailed'),
          }}
          onFile={r.photos.add}
          onRemove={r.photos.remove}
        />
      }
      submitLabel={r.isEdit ? t('rate.save') : t('rate.submit')}
      canSubmit={r.canSubmit}
      submitting={r.submitting}
      draftHint={r.draftSaved && !r.draftRestored ? t('common.draftSaved') : null}
      onSubmit={r.submit}
    />
  );
}
